import { useMemo } from 'react';
import { PiArrowUUpLeftDuotone, PiOfficeChairDuotone } from 'react-icons/pi';
import { Object3D } from 'three';

import { useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import { FCompositionNode, FNodeTypesEnum, FurnitureNode } from '@spazious/dollhouse-fsetup';
import { SchemaTypesEnum } from '@spazious/dollhouse-fsetup';
import { Translate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import {
  Flex,
  FlexProps,
  IconButton,
  Panel,
  PanelContent,
  PanelHeader,
  PanelProps,
  Typography,
} from '@spazious/dollhouse-uikit';
import { traverseUntil } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

import { useSelectedNodes } from '../../hooks/index.js';

import { InspectorFArrangement } from './InspectorFArrangement.js';
import { InspectorFurniture } from './InspectorFurniture.js';
import { MetaFieldsProvider } from './MetaFieldsProvider.js';

export interface InspectorPanelProps extends PanelProps {}

export interface InspectorFArrangementContentProps extends FlexProps {}

export interface InspectorSeatingContentProps extends FlexProps {}

const fSchemaMapping = Object.fromEntries(
  Object.entries(SchemaTypesEnum).map(([key, value]) => [value, key])
);

export const InspectorFArrangementContent: React.FC<InspectorFArrangementContentProps> = (
  props
) => {
  return (
    <Flex column gap="calc(2 * var(--spacing-padding))" {...props}>
      <InspectorFArrangement />
    </Flex>
  );
};

export const InspectorSeatingContent: React.FC<InspectorSeatingContentProps> = (props) => {
  const selectedNodes = useSelectedNodes();
  const { select } = useSelection();
  const scene = useDollhouse(({ scene }) => scene);
  const lookMode = useStore(({ controls }) => controls === ControlsModeEnum.Walk);

  const hasSameOriginator =
    (selectedNodes[0] as FurnitureNode).originator &&
    selectedNodes.every(
      (node) =>
        (node as FurnitureNode).originator === (selectedNodes[0] as FurnitureNode).originator
    );

  const handleSelectOriginatorClick = useEventCallback(() => {
    // @ts-ignore weird error from typescript parser
    const uuid = selectedNodes[0].originator.uuid;
    let foundNode: Object3D | null = null;

    traverseUntil<Object3D>(scene, (node) => {
      if (node.uuid === uuid) {
        foundNode = node;
        return false;
      }
    });
    if (foundNode) {
      select([foundNode]);
    }
  });

  return (
    <Flex column gap="calc(2 * var(--spacing-padding))" justifyContent="space-between" {...props}>
      {hasSameOriginator && !lookMode && (
        <Flex alignItems="center" gap="var(--spacing-padding)">
          <IconButton onClick={handleSelectOriginatorClick}>
            <PiArrowUUpLeftDuotone />
          </IconButton>
          <Typography size="medium" variant="title">
            <Translate>Back to setup settings</Translate>
          </Typography>
        </Flex>
      )}

      <MetaFieldsProvider>
        <InspectorFurniture gap="calc(2 * var(--spacing-padding))" justifyContent="space-between" />
      </MetaFieldsProvider>
    </Flex>
  );
};

export const InspectorPanel: React.FC<InspectorPanelProps> = (props) => {
  const selectedNodes = useSelectedNodes();
  const { select } = useSelection();
  const lookMode = useStore(({ controls }) => controls === ControlsModeEnum.Walk);

  const allFurnitureNodes = useMemo(
    () =>
      selectedNodes.length
        ? selectedNodes.every((nodes) => nodes.type === FNodeTypesEnum.Furniture)
        : false,
    [selectedNodes]
  );
  const isFCompositionNode = useMemo(
    () => selectedNodes.length === 1 && selectedNodes[0].type === FNodeTypesEnum.FComposition,
    [selectedNodes]
  );

  if (isFCompositionNode && lookMode) {
    select([]);
  }

  const header = useMemo(() => {
    if (!allFurnitureNodes && !isFCompositionNode) {
      return null;
    }
    if (allFurnitureNodes) {
      return (
        <Typography size="large" variant="title">
          <Translate>Furniture</Translate>
        </Typography>
      );
    }

    if (isFCompositionNode) {
      const node = selectedNodes[0] as FCompositionNode;
      return (
        <Typography size="large" variant="title">
          <Flex alignItems="center" gap="8px">
            <PiOfficeChairDuotone />
            {`${(fSchemaMapping as any)[node.id & 0xff].replace(/^(F(Arrangement|Ensemble))/, '')}`}
          </Flex>
        </Typography>
      );
    }
  }, [allFurnitureNodes, isFCompositionNode, selectedNodes]);

  const panel = useMemo(() => {
    if (allFurnitureNodes) {
      return <InspectorSeatingContent />;
    }

    if (isFCompositionNode) {
      return <InspectorFArrangementContent />;
    }

    return (
      <Flex alignItems="center" margin="calc(var(--spacing-padding)* 6) 0 0 0">
        <Typography size="medium" variant="title">
          <Translate>Please select a furniture or setup to display seating info</Translate>
        </Typography>
      </Flex>
    );
  }, [allFurnitureNodes, isFCompositionNode]);

  return (
    <Panel {...props}>
      <PanelHeader>
        <Flex gap="var(--spacing-padding)">{header}</Flex>
      </PanelHeader>
      <PanelContent>{panel}</PanelContent>
    </Panel>
  );
};
